import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { products, type PermGroupRow, type PermMapRow } from "@/api/client";
import { Select } from "@/components/inputs";
import { qk, usePermissionMaps } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

/** The permission vocabulary a product's tools use.
 *
 *  PER (PRODUCT, TOOL), not per product. D-89 recorded "the map goes to kind"
 *  and was incomplete: Cluster carries BOTH a `uds_tool` map (6 groups, 21
 *  permissions) and an `eol_tool` map (1 group, 5). The same integer means
 *  different things in different tools, which is exactly why nothing outside
 *  a product may reason about the numbers.
 *
 *  THE NUMBERS ARE THE CONTRACT. Every deployed bench compares against these
 *  integers, so a permission's id is NOT editable once it exists — changing
 *  what 207 means silently changes what every bench holding it may do, with
 *  no error anywhere, because the tool simply believes the number. Deprecate
 *  and add a new one; the page offers exactly those two actions.
 *
 *  SUPER ADMIN ONLY, per Dattatray. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the vocabulary and no controls.
 */
export function PermissionMapPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [tool, setTool] = useState("");
  const q = usePermissionMaps(kind);
  const [addMap, setAddMap] = useState(false);
  const [addGroupTo, setAddGroupTo] = useState<PermMapRow | null>(null);
  const [addPermTo, setAddPermTo] = useState<PermGroupRow | null>(null);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Permission map"
        title="Permission map"
        desc="The vocabulary a product's tools use, per tool. The numbers are what a bench compares against, so they are never re-pointed — deprecate and add instead."
      />

      {/* SECOND SELECTOR IS THE TOOL, NOT A MODEL. Dattatray confirmed the
          key: "(product, TOOL) is correct. We decided that we can allocate
          Tool release to a specific model — I think that was the catch (its
          part of tool release rule)." So model targeting lives on RELEASES;
          a map belongs to a product and a tool. */}
      <ScopeBar right={mayAuthor ? (
        <button className={addMap ? "btn ghost sm" : "btn teal sm"}
          disabled={!kind} onClick={() => setAddMap(!addMap)}>
          {addMap ? "Cancel" : "Add a tool map"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={(k) => { setKind(k); setTool(""); }} />
        <ScopeField label="Tool"
          hint="the same number means different things in different tools">
          <Select value={tool} onChange={(e) => setTool(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            <option value="">All tools</option>
            {(q.data?.maps ?? []).map((m) => (
              <option key={m.map_id} value={m.tool}>{m.name}</option>
            ))}
          </Select>
        </ScopeField>
      </ScopeBar>

      {addMap && <AddMapRow kind={kind} onDone={() => setAddMap(false)} />}

      {!kind && <ChoosePrompt what="permission map" />}
      {kind && q.isLoading && <div className="panel panel-pad cap">Loading…</div>}
      {kind && !q.isLoading && (q.data?.maps.length ?? 0) === 0 && (
        <div className="panel panel-pad cap">
          No permission map for this product yet.
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {(q.data?.maps ?? []).filter((m) => !tool || m.tool === tool).map((m) => (
          <div key={m.map_id} className="panel panel-pad">
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <strong>{m.name}</strong>
              <code className="cap">{m.tool}</code>
              <Chip tone="mute">{m.permission_count} permissions</Chip>
              {mayAuthor && (
                <button className="btn ghost sm" style={{ marginLeft: "auto" }}
                  onClick={() => setAddGroupTo(addGroupTo?.map_id === m.map_id ? null : m)}>
                  {addGroupTo?.map_id === m.map_id ? "Cancel" : "Add group"}
                </button>
              )}
            </div>

            {addGroupTo?.map_id === m.map_id && (
              <div style={{ marginTop: 10 }}>
                <AddGroupRow kind={kind} map={m} onDone={() => setAddGroupTo(null)} />
              </div>
            )}

            {m.groups.map((g) => (
              <div key={g.group_uid ?? g.group_id} style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: "var(--mono)" }}>{g.group_id}</span>
                  <strong>{g.name}</strong>
                  {g.deprecated && <Chip tone="mute">deprecated</Chip>}
                  {mayAuthor && (
                    <button className="btn ghost sm" style={{ marginLeft: "auto" }}
                      onClick={() => setAddPermTo(
                        addPermTo?.group_uid === g.group_uid ? null : g)}>
                      {addPermTo?.group_uid === g.group_uid ? "Cancel" : "Add permission"}
                    </button>
                  )}
                </div>

                {addPermTo?.group_uid === g.group_uid && (
                  <div style={{ marginTop: 8, marginLeft: 14 }}>
                    <AddPermissionRow kind={kind} group={g}
                      onDone={() => setAddPermTo(null)} />
                  </div>
                )}
                <div style={{ display: "grid", gap: 2, marginTop: 4, marginLeft: 14 }}>
                  {g.permissions.map((p) => (
                    <Permission key={p.perm_uid ?? p.permission_id} kind={kind}
                      perm={p} mayAuthor={mayAuthor} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

    </div>
  );
}

function Permission({ kind, perm, mayAuthor }: {
  kind: string; perm: { permission_id: number; perm_uid?: string; name: string; deprecated: boolean };
  mayAuthor: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => products.updatePermission(kind, perm.perm_uid ?? "",
      { deprecated: !perm.deprecated }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.maps(kind) }),
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontFamily: "var(--mono)", minWidth: 60 }}>{perm.permission_id}</span>
      <span style={{ textDecoration: perm.deprecated ? "line-through" : undefined }}>
        {perm.name}
      </span>
      {mayAuthor && perm.perm_uid && (
        <button className="btn ghost sm" style={{ marginLeft: "auto" }}
          disabled={toggle.isPending} onClick={() => toggle.mutate()}>
          {perm.deprecated ? "Restore" : "Deprecate"}
        </button>
      )}
    </div>
  );
}

/** The three authoring forms, all INLINE. Dattatray: "No Pop up."
 *
 *  A modal was wrong here for a specific reason, not just a style one: each
 *  of these adds something INTO a thing already on screen — a map into a
 *  product, a group into a map, a permission into a group. Covering that
 *  thing with a dialog hides the context the number has to be unique within,
 *  which is exactly what the operator needs to see while typing it.
 */
function InlineForm({ children, onCancel, onGo, go, busy, err }: {
  children: React.ReactNode; onCancel: () => void; onGo: () => void;
  go: string; busy: boolean; err: unknown;
}) {
  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      {children}
      <ScopeAction>
        <span style={{ display: "flex", gap: 8 }}>
          <button className="btn teal sm" disabled={busy} onClick={onGo}>
            {busy ? "Saving…" : go}
          </button>
          <button className="btn ghost sm" onClick={onCancel}>Cancel</button>
        </span>
      </ScopeAction>
      {err ? (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(err as Error).message}
        </div>
      ) : null}
    </div>
  );
}

function AddMapRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [tool, setTool] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addMap(kind, { tool, name: name || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add map"
      busy={!tool || add.isPending} err={add.error}>
      <ScopeField label="Tool slug" hint="what the tool identifies itself as">
        <SearchInput value={tool} autoFocus mono placeholder="uds_tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTool(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="UDS Tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddGroupRow({ kind, map, onDone }: {
  kind: string; map: PermMapRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [gid, setGid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addGroup(kind, map.map_id, { group_id: Number(gid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add group"
      busy={!/^\d+$/.test(gid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Group number" hint={`unique within ${map.tool}`}>
        <SearchInput value={gid} autoFocus mono placeholder="200" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Flashing"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddPermissionRow({ kind, group, onDone }: {
  kind: string; group: PermGroupRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [pid, setPid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addPermission(kind, group.group_uid ?? "",
      { permission_id: Number(pid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add permission"
      busy={!/^\d+$/.test(pid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Permission number"
        hint="unique across the WHOLE map — a bench gets a flat set. Permanent.">
        <SearchInput value={pid} autoFocus mono placeholder="207" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Write DID"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}
